import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { authorizedFetch } from "../auth/authorizedFetch";
import { throwIfNotOk } from "./errorEnvelope";
import type {
  ClientManifest,
  ClientManifestExport,
  ClientManifestResult,
} from "./types";

const API_BASE = import.meta.env.VITE_KEYCLOAK_API_URL ?? "/keycloak-api";

// The live registration of one client, rendered fresh on every view (ADR-0050).
export function useClientManifestExport(clientId: string) {
  return useQuery({
    queryKey: ["client-manifests", clientId],
    enabled: !!clientId,
    queryFn: async () => {
      const res = await authorizedFetch(
        `${API_BASE}/ClientManifests?clientId=${encodeURIComponent(clientId)}`,
      );
      await throwIfNotOk(res, "kc");
      return res.json() as Promise<ClientManifestExport>;
    },
  });
}

async function postManifests(
  action: "plan" | "apply",
  manifests: ClientManifest[],
  platformId?: string,
) {
  const query = platformId
    ? `?platformId=${encodeURIComponent(platformId)}`
    : "";
  const res = await authorizedFetch(
    `${API_BASE}/ClientManifests/${action}${query}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(manifests),
    },
  );
  await throwIfNotOk(res, "kc");
  return res.json() as Promise<ClientManifestResult>;
}

// Judges every manifest and writes nothing.
export function planClientManifests(
  manifests: ClientManifest[],
  platformId?: string,
) {
  return postManifests("plan", manifests, platformId);
}

export function usePlanClientManifests() {
  return useMutation({
    mutationFn: (manifests: ClientManifest[]) => planClientManifests(manifests),
  });
}

// A 200 even on partial success: read the per-client outcomes.
export function useApplyClientManifests() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (manifests: ClientManifest[]) =>
      postManifests("apply", manifests),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["client-manifests"] });
      queryClient.invalidateQueries({ queryKey: ["platforms"] });
    },
  });
}
